import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Heading,
  Text,
  useToast,
  Spinner,
  Button,
  Select,
  HStack,
  IconButton,
  Tooltip,
  Badge,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import {
  getDashboardData,
  API,
  enableModelForAll,
	  deleteUser,
	  createSuperAdminUser,
	  setSuperAdminUserPassword,
	  startRagSync,
	  getRagSyncStatus,
	} from '../services/API';
import DeleteUserModal from './DeleteUserModal';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modelAccess, setModelAccess] = useState({});
  const [selectedModelToEnable, setSelectedModelToEnable] = useState('');
	  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	  const [userToDelete, setUserToDelete] = useState(null);
	  const [isDeleting, setIsDeleting] = useState(false);
	  const [ragSyncStatus, setRagSyncStatus] = useState(null);
	  const [isStartingRagSync, setIsStartingRagSync] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });

  const fetchUsers = async () => {
    try {
      const response = await getDashboardData();
      if (response.success) {
        setUsers(response.data.users);

        // Extract all model display names from the nested structure
        const modelsList = response.data.available_models.flatMap(provider =>
          provider.models.map(model => ({
            id: model.id,
            display_name: model.display_name,
            provider_name: provider.display_name
          }))
        );

        setAvailableModels(modelsList);
        if (modelsList.length > 0) {
          setSelectedModelToEnable(modelsList[0].id);
        }

        // Initialize model access state for all users
        const initialAccess = {};
        response.data.users.forEach(user => {
          initialAccess[user.id] = {};
          modelsList.forEach(model => {
            initialAccess[user.id][model.id] = user.available_models.includes(model.id);
          });
        });
        setModelAccess(initialAccess);
      } else {
        toast({
          title: 'Access denied',
          description: response.error,
          status: 'error',
          duration: 3000,
        });
        navigate('/chat');
      }
    } catch (error) {
      toast({
        title: 'Failed to fetch users data',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
      navigate('/chat');
    } finally {
      setLoading(false);
    }
  };

	  useEffect(() => {
    const checkSuperAdminAccess = async () => {
      try {
        const response = await API.get('/superadmin');
        if (response.data.status === 'success' && response.data.is_superadmin) {
          await fetchUsers();
        } else {
          navigate('/chat');
        }
      } catch (error) {
        console.error('SuperAdmin check error:', error);
        navigate('/chat');
      }
    };

    checkSuperAdminAccess();
	  }, [navigate]);

	  useEffect(() => {
	    let timer;
	    if (ragSyncStatus?.status === 'running') {
	      timer = setInterval(async () => {
	        const result = await getRagSyncStatus();
	        if (result.success) {
	          setRagSyncStatus(result.data);
	        }
	      }, 5000);
	    }
	    return () => {
	      if (timer) {
	        clearInterval(timer);
	      }
	    };
	  }, [ragSyncStatus?.status]);

  const handleModelAccessChange = (userId, modelId) => {
    setModelAccess(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [modelId]: !prev[userId][modelId]
      }
    }));
  };

	  const handleEnableModelForAll = async () => {
    if (!selectedModelToEnable) {
      toast({
        title: 'Error',
        description: 'Please select a model first',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      const result = await enableModelForAll(selectedModelToEnable);
      if (result.success) {
        toast({
          title: 'Success',
          description: 'Model has been enabled for all users',
          status: 'success',
          duration: 3000,
        });
        // Refresh the data
        await fetchUsers();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to enable model for all users',
        status: 'error',
        duration: 3000,
      });
    }
	  };

	  const formatRagSyncSummary = (status) => {
	    if (!status) return 'Not synced in this session';
	    if (status.status === 'running') return 'Sync running';
	    if (status.status === 'failed') return status.error || 'Sync failed';
	    const summary = status.summary;
	    if (!summary) return status.status || 'Idle';
	    return `Drive ${summary.drive_documents || 0}, active ${summary.active_documents || 0}, downloaded ${summary.downloaded || 0}, uploaded ${summary.uploaded || 0}, replaced ${summary.replaced || 0}, skipped ${summary.skipped || 0}, removed ${summary.removed || 0}, failed ${summary.failed || 0}`;
	  };

	  const handleStartRagSync = async () => {
	    setIsStartingRagSync(true);
	    const result = await startRagSync();
	    setIsStartingRagSync(false);

	    if (result.success) {
	      setRagSyncStatus(result.data);
	      toast({
	        title: 'PDF sync started',
	        description: result.message,
	        status: 'success',
	        duration: 3500,
	      });
	      return;
	    }

	    toast({
	      title: 'PDF sync failed',
	      description: result.error,
	      status: 'error',
	      duration: 5000,
	    });
	  };

  const handleSaveAllChanges = async () => {
    try {
      // Create array of promises for all user updates
      const updatePromises = users.map(user => {
        const selectedModels = Object.entries(modelAccess[user.id])
          .filter(([_, isSelected]) => isSelected)
          .map(([modelId]) => modelId);

        return API.put(`/users/${user.id}/models`, {
          models: selectedModels
        });
      });

      // Wait for all updates to complete
      const responses = await Promise.all(updatePromises);

      // Check if all updates were successful
      const allSuccessful = responses.every(response => response.data.status === 'success');

      if (allSuccessful) {
        // Update local state with new data
        const updatedUsers = users.map((user, index) => ({
          ...user,
          available_models: responses[index].data.data.available_models
        }));
        setUsers(updatedUsers);

        toast({
          title: 'Access updated',
          description: 'All user model access has been updated successfully',
          status: 'success',
          duration: 3000,
        });
      } else {
        throw new Error('Some updates failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Failed to update access',
        description: 'An error occurred while updating access',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDeleteClick = (user) => {
    if (user.is_superadmin) {
      toast({
        title: 'Cannot Delete Superadmin',
        description: 'Superadmin users cannot be deleted for security reasons',
        status: 'error',
        duration: 5000,
      });
      return;
    }
    
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      const result = await deleteUser(userToDelete.id);
      if (result.success) {
        toast({
          title: 'User Deleted',
          description: result.message,
          status: 'success',
          duration: 5000,
        });
        
        setUsers(prevUsers => prevUsers.filter(u => u.id !== userToDelete.id));
        
        setModelAccess(prev => {
          const newState = { ...prev };
          delete newState[userToDelete.id];
          return newState;
        });
        
        setDeleteModalOpen(false);
        setUserToDelete(null);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: error.message || 'Failed to delete user',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) {
      setDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const handleCreateUser = async () => {
    if (!createUserForm.name || !createUserForm.email || !createUserForm.password) {
      toast({
        title: 'Missing fields',
        description: 'Name, email, and password are required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const result = await createSuperAdminUser(createUserForm);
    if (result.success) {
      toast({
        title: 'User created',
        description: `${result.data.username} has been created`,
        status: 'success',
        duration: 3500,
      });
      setCreateUserForm({ name: '', email: '', password: '', role: 'user' });
      await fetchUsers();
      return;
    }

    toast({
      title: 'Create user failed',
      description: result.error,
      status: 'error',
      duration: 4000,
    });
  };

  const handleSetTempPassword = async (user) => {
    const newPassword = window.prompt(`Set a temporary password for ${user.username}:`);
    if (!newPassword) {
      return;
    }

    const result = await setSuperAdminUserPassword(user.id, newPassword, true);
    if (result.success) {
      toast({
        title: 'Password updated',
        description: `Password updated for ${user.username}`,
        status: 'success',
        duration: 3500,
      });
      await fetchUsers();
      return;
    }

    toast({
      title: 'Password update failed',
      description: result.error,
      status: 'error',
      duration: 4000,
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box 
      p={6} 
      h="100vh" 
      display="flex" 
      flexDirection="column"
      overflow="hidden"
    >
      <HStack justify="space-between" mb={6}>
        <Heading>Super Admin Dashboard</Heading>
      </HStack>

	      <Box mb={6} bg="#f8fafc" p={4} borderRadius="md" border="1px solid #e2e8f0">
        <Text fontWeight="700" mb={3}>Create User</Text>
        <HStack align="flex-end" spacing={3} flexWrap="wrap">
          <FormControl maxW="240px">
            <FormLabel fontSize="sm">Name</FormLabel>
            <Input
              value={createUserForm.name}
              onChange={(e) => setCreateUserForm((prev) => ({ ...prev, name: e.target.value }))}
              bg="#ffffff"
            />
          </FormControl>
          <FormControl maxW="260px">
            <FormLabel fontSize="sm">Email</FormLabel>
            <Input
              type="email"
              value={createUserForm.email}
              onChange={(e) => setCreateUserForm((prev) => ({ ...prev, email: e.target.value }))}
              bg="#ffffff"
            />
          </FormControl>
          <FormControl maxW="220px">
            <FormLabel fontSize="sm">Password</FormLabel>
            <Input
              type="password"
              value={createUserForm.password}
              onChange={(e) => setCreateUserForm((prev) => ({ ...prev, password: e.target.value }))}
              bg="#ffffff"
            />
          </FormControl>
          <FormControl maxW="180px">
            <FormLabel fontSize="sm">Role</FormLabel>
            <Select
              value={createUserForm.role}
              onChange={(e) => setCreateUserForm((prev) => ({ ...prev, role: e.target.value }))}
              bg="#ffffff"
            >
              <option value="user">User</option>
              <option value="super_admin">Super Admin</option>
            </Select>
          </FormControl>
          <Button colorScheme="blue" onClick={handleCreateUser}>Create User</Button>
        </HStack>
	      </Box>

	      <Box mb={6} bg="#f8fafc" p={4} borderRadius="md" border="1px solid #e2e8f0">
	        <HStack justify="space-between" align="center" flexWrap="wrap" spacing={3}>
	          <Box>
	            <Text fontWeight="700">Google Drive PDF Index</Text>
	            <Text fontSize="sm" color="#475569">{formatRagSyncSummary(ragSyncStatus)}</Text>
	          </Box>
	          <Button
	            colorScheme="teal"
	            onClick={handleStartRagSync}
	            isLoading={isStartingRagSync || ragSyncStatus?.status === 'running'}
	            loadingText={ragSyncStatus?.status === 'running' ? 'Syncing' : 'Starting'}
	          >
	            Sync PDFs from Drive
	          </Button>
	        </HStack>
	      </Box>

	      {/* Wrapper for horizontal scroll */}
      <Box 
        position="relative" 
        overflowX="auto" 
        overflowY="auto"
        flex="1"
      >
        {/* Fixed position container for the header content */}
        <Box
          position="sticky"
          left="0"
          right="0"
          zIndex="1"
          bg="inherit"
          pb={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text fontSize="lg" fontWeight="bold">Total Users: {users.length}</Text>
          <HStack spacing={3}>
            <Select
              value={selectedModelToEnable}
              onChange={(e) => setSelectedModelToEnable(e.target.value)}
              bg="#ffffff"
              color="#0f172a"
              borderColor="#cbd5e1"
              width="200px"
              size="sm"
              borderRadius="md"
              _hover={{ borderColor: '#94a3b8' }}
              _focus={{ borderColor: '#3498db', boxShadow: '0 0 0 1px #3498db' }}
              sx={{
                '& option': {
                  bg: '#ffffff',
                  color: '#0f172a',
                },
                '& option:checked': {
                  bg: '#e2e8f0',
                  color: '#0f172a',
                },
              }}
            >
              {availableModels.map(model => (
                <option key={model.id} value={model.id}>
                  {model.display_name}
                </option>
              ))}
            </Select>
            <Button
              colorScheme="green"
              size="sm"
              onClick={handleEnableModelForAll}
              borderRadius="md"
            >
              Enable for All
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={handleSaveAllChanges}
              borderRadius="md"
            >
              Save Changes
            </Button>
          </HStack>
        </Box>

        {/* Table with minimum width to ensure proper scrolling */}
        <Box minWidth="1200px">
          <Table
            variant="simple"
            sx={{
              'th, td': {
                color: '#0f172a',
              },
              th: {
                textTransform: 'none',
                fontWeight: 600,
              },
              '.chakra-checkbox__control': {
                borderColor: '#64748b',
                bg: '#ffffff',
              },
              '.chakra-checkbox__control[data-checked]': {
                bg: '#2563eb',
                borderColor: '#2563eb',
              },
              '.chakra-checkbox__control svg': {
                color: '#ffffff',
              },
            }}
          >
            <Thead>
              <Tr>
                <Th>Username</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Select All</Th>
                {availableModels.map(model => (
                  <Th key={model.id}>{model.display_name}</Th>
                ))}
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((user) => (
                <Tr key={user.id}>
                  <Td>{user.username}</Td>
                  <Td>{user.email}</Td>
                  <Td>
                    <Badge colorScheme={user.role === 'super_admin' ? 'purple' : 'blue'}>
                      {user.role}
                    </Badge>
                  </Td>
                  <Td>
                    {(() => {
                      const selections = Object.values(modelAccess[user.id] || {});
                      const allSelected = selections.length > 0 && selections.every(Boolean);
                      const someSelected = selections.some(Boolean);
                      return (
                        <Checkbox
                          colorScheme="blue"
                          isChecked={allSelected}
                          isIndeterminate={!allSelected && someSelected}
                          onChange={() => {
                            const nextValue = !allSelected;
                            setModelAccess(prev => ({
                              ...prev,
                              [user.id]: availableModels.reduce((acc, model) => ({
                                ...acc,
                                [model.id]: nextValue
                              }), {})
                            }));
                          }}
                          aria-label={allSelected ? "Deselect all models for user" : "Select all models for user"}
                        />
                      );
                    })()}
                  </Td>
                  {availableModels.map(model => (
                    <Td key={model.id}>
                      <Checkbox
                        isChecked={modelAccess[user.id]?.[model.id] || false}
                        onChange={() => handleModelAccessChange(user.id, model.id)}
                      />
                    </Td>
                  ))}
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip 
                        label={user.is_superadmin ? "Cannot delete superadmin user" : "Delete User"} 
                        hasArrow 
                        placement="top"
                      >
                        <IconButton
                          icon={<DeleteIcon />}
                          colorScheme="red"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteClick(user)}
                          aria-label="Delete user"
                          isDisabled={user.is_superadmin}
                          _hover={user.is_superadmin ? {} : { bg: 'red.100' }}
                          opacity={user.is_superadmin ? 0.4 : 1}
                          cursor={user.is_superadmin ? "not-allowed" : "pointer"}
                        />
                      </Tooltip>
                      <Tooltip label="Set Temporary Password" hasArrow placement="top">
                        <Button size="xs" colorScheme="orange" variant="ghost" onClick={() => handleSetTempPassword(user)}>
                          Reset Password
                        </Button>
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Delete User Modal */}
      <DeleteUserModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        user={userToDelete}
        isDeleting={isDeleting}
      />
    </Box>
  );
};

export default SuperAdminDashboard;
